import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Sparkles, ShieldAlert, FileText, Globe } from "lucide-react";
import AnswerText from "@/components/AnswerText";
import SourceSheet from "@/components/SourceSheet";
import AssistantAvatar from "@/components/AssistantAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  tr: {
    notFoundTitle: "Asistan bulunamadı",
    notFoundBody: "Bu yayın linki geçersiz veya kaldırılmış olabilir.",
    defaultName: "Bilgi Asistanı",
    emptyTitle: "Size nasıl yardımcı olabilirim?",
    welcome: "Sorularınızı yanıtlarım; cevaplarım her zaman kaynaklarıyla birlikte gelir.",
    insufficient: "Kaynaklarda yeterli bilgi yok",
    error: "Bir hata oluştu. Lütfen tekrar deneyin.",
    unavailable: "Hizmet şu anda geçici olarak kullanılamıyor. Lütfen birkaç dakika sonra tekrar deneyin.",
    placeholder: "Sorunuzu yazın...",
    preparing: "Yanıt hazırlanıyor...",
    poweredBy: "Verba ile güçlendirilmiştir",
    page: "s.",
  },
  en: {
    notFoundTitle: "Assistant not found",
    notFoundBody: "This link may be invalid or has been removed.",
    defaultName: "Knowledge Assistant",
    emptyTitle: "How can I help you?",
    welcome: "I answer your questions; my answers always come with their sources.",
    insufficient: "Not enough information in the sources",
    error: "Something went wrong. Please try again.",
    unavailable: "The service is temporarily unavailable. Please try again in a few minutes.",
    placeholder: "Type your question...",
    preparing: "Preparing an answer...",
    poweredBy: "Powered by Verba",
    page: "p.",
  },
};

function pickLang(metaLang) {
  if (metaLang === "en") return "en";
  if (metaLang === "tr") return "tr";
  return (navigator.language || "").toLowerCase().startsWith("en") ? "en" : "tr";
}

export default function HostedChat() {
  const { slug } = useParams();
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const endRef = useRef();
  const uiLang = pickLang(meta?.language);
  const t = STRINGS[uiLang];
  const langBadge = meta?.language === "en" ? "EN" : meta?.language === "tr" ? "TR" : "TR / EN";

  useEffect(() => {
    fetch(`${API}/public/assistants/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setMeta)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (preset) => {
    const q = (typeof preset === "string" ? preset : input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/assistants/${slug}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      if (!res.ok) throw new Error(res.status === 502 || res.status === 503 ? "unavailable" : "error");
      const r = await res.json();
      setSessionId(r.session_id);
      setMessages((m) => [...m, {
        role: "assistant", content: r.answer, answerable: r.answerable,
        confidence: r.confidence, citations: r.citations, animate: true,
      }]);
    } catch (e) {
      const text = e?.message === "unavailable" ? t.unavailable : t.error;
      setMessages((m) => [...m, { role: "assistant", content: text, answerable: true, citations: [], isError: true }]);
    } finally { setLoading(false); }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/30 grain">
        <div className="text-center">
          <h1 className="text-2xl font-bold">{t.notFoundTitle}</h1>
          <p className="mt-2 text-muted-foreground">{t.notFoundBody}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex min-h-[100dvh] flex-col ${embed ? "bg-background" : "bg-secondary/20 grain"}`}>
      {/* header */}
      <header className={`sticky top-0 z-10 border-b border-border bg-background/80 safe-x backdrop-blur-xl ${embed ? "" : "pt-safe"}`}>
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4">
          <AssistantAvatar icon={meta?.icon} color={meta?.accent_color || "#0a0a0a"} size={36} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-bold leading-tight">{meta?.name || t.defaultName}</span>
              <span
                data-testid="lang-badge"
                title={meta?.language === "auto" ? "Otomatik dil" : undefined}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
              >
                <Globe className="h-3 w-3" /> {langBadge}
              </span>
            </div>
            {meta?.description && <div className="truncate text-xs text-muted-foreground">{meta.description}</div>}
          </div>
        </div>
      </header>

      {/* messages */}
      <div className="mx-auto w-full max-w-3xl flex-1 space-y-6 px-5 py-8">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <AssistantAvatar icon={meta?.icon} color={meta?.accent_color || "#0a0a0a"} size={56} />
            <h2 className="mt-5 text-2xl font-extrabold tracking-tight">{t.emptyTitle}</h2>
            <p className="mt-2 max-w-md text-muted-foreground">
              {meta?.welcome_message || t.welcome}
            </p>
            {meta?.suggested_questions?.length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {meta.suggested_questions.map((qq, i) => (
                  <button
                    key={i}
                    data-testid={`suggested-q-${i}`}
                    onClick={() => send(qq)}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground transition-all hover:border-foreground/40 hover:bg-secondary"
                  >
                    {qq}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className="fade-up">
            {m.role === "user" ? (
              <div className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-md bg-foreground px-4 py-2.5 text-sm text-background">{m.content}</div>
              </div>
            ) : (
              <div className="max-w-[92%]">
                <div className={`rounded-2xl rounded-tl-md border px-4 py-3 text-[15px] leading-relaxed ${
                  m.answerable ? "border-border bg-background" : "border-amber-200 bg-amber-50"
                }`}>
                  {!m.answerable && (
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                      <ShieldAlert className="h-3.5 w-3.5" /> {t.insufficient}
                    </div>
                  )}
                  <AnswerText
                    text={m.content}
                    citations={m.citations}
                    animate={m.animate}
                    onTick={() => endRef.current?.scrollIntoView({ behavior: "smooth" })}
                    onCite={setActiveCite}
                    className="text-[15px] leading-relaxed"
                  />
                </div>
                {m.citations?.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {m.citations.map((c, idx) => (
                      <button
                        key={idx}
                        type="button"
                        data-testid={`citation-chip-${i}-${idx}`}
                        onClick={() => setActiveCite(c)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-secondary hover:text-foreground"
                      >
                        {c.n != null && (
                          <span className="mono flex h-4 w-4 items-center justify-center rounded bg-foreground/10 text-[10px] font-semibold text-foreground/70">
                            {c.n}
                          </span>
                        )}
                        <FileText className="h-3 w-3" />
                        <span className="max-w-[220px] truncate">
                          {c.document}{c.page != null ? ` · ${t.page}${c.page}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 animate-pulse" />
            <span className="animate-pulse">{t.preparing}</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* input */}
      <div className="sticky bottom-0 border-t border-border bg-background/80 pb-safe safe-x backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-end gap-2 px-5 py-4">
          <textarea
            data-testid="public-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t.placeholder}
            rows={1}
            className="max-h-32 min-h-[48px] flex-1 resize-none rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring/40"
          />
          <button
            data-testid="public-send-btn"
            onClick={send}
            disabled={loading}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-foreground text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <Send className="h-4.5 w-4.5" />
          </button>
        </div>
        <div className="pb-3 text-center text-[11px] text-muted-foreground">{t.poweredBy}</div>
      </div>

      <SourceSheet cite={activeCite} onClose={() => setActiveCite(null)} lang={uiLang} />
    </div>
  );
}
